/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { MediaObject } from "../../types/MediaObject";
import LegalbirdIoModal from "../Modal/LegalbirdIoModal";
import React, { Dispatch, SetStateAction, useState } from "react";
import { Alert, List, ListItem, MenuItem, Select, Stack, Typography } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import LegalbirdTipTapEditorProvider from "../LbEditor/LegalbirdTipTapEditorProvider";
import LegalbirdTipTapEditor from "../LbEditor/LegalbirdTipTapEditor";
import { useCurrentEditor } from "@tiptap/react";
import { getEditorHtml } from "../../services/Editor/editorFunctions";
import { useCase } from "../../provider/CaseProvider";
import PreviewButton from "../Preview/PreviewButton";
import { ActionType } from "../../types/SummarizeAndInformMediaObject";
import SummarizeAndInformMediaObjectSubmitButton from "./SummarizeAndInformMediaObjectModalSubmitButton";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";

type SummarizeAndInformMediaObjectModalInnerProps = {
  mediaObject: MediaObject;
  summarizeAndInformOpen: boolean;
  setSummarizeAndInformOpen: Dispatch<SetStateAction<boolean>>;
};

export default function SummarizeAndInformMediaObjectModal(props: SummarizeAndInformMediaObjectModalInnerProps) {
  return (
    <LegalbirdTipTapEditorProvider>
      <SummarizeAndInformMediaObjectModalInner {...props} />
    </LegalbirdTipTapEditorProvider>
  );
}

function SummarizeAndInformMediaObjectModalInner({
  mediaObject,
  summarizeAndInformOpen,
  setSummarizeAndInformOpen,
}: SummarizeAndInformMediaObjectModalInnerProps) {
  const [currentActionType, setCurrentActionType] = useState<ActionType | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSelectDisabled, setIsSelectDisabled] = useState(false);

  const { editor } = useCurrentEditor();
  const { product } = useCase();

  const { data: summarizeAndInformIdentifiers } = useQuery({
    queryKey: queryKeys.collection("/lb/services/summarize_and_inform/identifiers"),
    queryFn: () => fetchCollection("/lb/services/summarize_and_inform/identifiers")
  });
  const isAllowedToSummarizeAndInform = summarizeAndInformIdentifiers?.includes(mediaObject.actionIdentifier ?? "");

  const hasSummary =
    currentActionType &&
    [ActionType.FINISH_EXTERNAL_SUMMARY, ActionType.FINISH_INTERNAL_SUMMARY].includes(currentActionType);

  const documentIdentifier =
    currentActionType === ActionType.FINISH_EXTERNAL_SUMMARY ? "externalSummary" : "internalSummary";

  if (!product) {
    return null;
  }

  return (
    <LegalbirdIoModal
      title={"Zusammenfassen & Informieren"}
      open={summarizeAndInformOpen}
      handleClose={() => setSummarizeAndInformOpen(false)}
      maxWidth={"md"}
      fullWidth={true}
      submitButton={
        <Stack sx={{
          gap: "1rem"
        }}>
          {Boolean(hasSummary) && (
            <PreviewButton
              product={product}
              documentIdentifier={documentIdentifier}
              buttonText={"Vorschau anzeigen"}
              params={{
                summary: getEditorHtml(editor),
                mediaObjectId: mediaObject.id,
              }}
            />
          )}
          {
            <SummarizeAndInformMediaObjectSubmitButton
              currentActionType={currentActionType}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              setIsSelectDisabled={setIsSelectDisabled}
              setCurrentActionType={setCurrentActionType}
              setSummarizeAndInformOpen={setSummarizeAndInformOpen}
              editor={editor}
              mediaObjectId={mediaObject.id}
              documentIdentifier={documentIdentifier}
            />
          }
        </Stack>
      }
    >
      <Stack
        sx={{
          gap: "1rem",
          padding: "0.5rem 0"
        }}>
        {!Boolean(currentActionType) && (
          <Alert variant={"outlined"} severity={"info"}>
            <Typography sx={{
              fontWeight: "bold"
            }}>Gewünschte Bearbeitungsform wählen:</Typography>
            <List sx={{ listStyleType: "disc", paddingLeft: "1.5rem", paddingBottom: "0" }}>
              <ListItem sx={{ display: "list-item" }}>
                <Typography component="span">
                  <strong>Nur Originaldokument versenden:</strong>
                </Typography>{" "}
                <Typography component="span">
                  Standard-Nachricht ohne AI-Zusammenfassung an Mandantschaft schicken
                </Typography>
              </ListItem>
              <ListItem sx={{ display: "list-item" }}>
                <Typography component="span">
                  <strong>Zusammenfassung für Mandantschaft:</strong>
                </Typography>{" "}
                <Typography component="span">
                  Erstellt eine verständliche Zusammenfassung als Dokument und versendet dieses inkl. dem
                  Originaldokument direkt per Nachricht an die Mandantschaft.
                </Typography>
              </ListItem>
              <ListItem sx={{ display: "list-item", paddingBottom: "0" }}>
                <Typography component="span">
                  <strong>Interne Zusammenfassung:</strong>
                </Typography>{" "}
                <Typography component="span">
                  Erstellt eine juristische Analyse für die interne Aktenbearbeitung (Fokus auf Fristen, Risiken &
                  Kernargumente). Es erfolgt keine Information an die Mandantschaft.
                </Typography>
              </ListItem>
            </List>
          </Alert>
        )}
        <FormControl fullWidth>
          <InputLabel>Art der Zusammenfassung</InputLabel>
          <Select
            fullWidth
            label={"Art der Zusammenfassung"}
            name={"summarizeAndInformType"}
            defaultValue={""}
            onChange={({ target }) => setCurrentActionType(target.value as ActionType)}
            disabled={isLoading || isSelectDisabled}
          >
            {isAllowedToSummarizeAndInform && [
              <MenuItem key="original" value={ActionType.ORIGINAL}>
                Originaldokument versenden
              </MenuItem>,
              <MenuItem key="external" value={ActionType.GENERATE_EXTERNAL_SUMMARY}>
                Zusammenfassung für Mandantschaft
              </MenuItem>,
            ]}
            <MenuItem value={ActionType.GENERATE_INTERNAL_SUMMARY}>Interne Zusammenfassung</MenuItem>
          </Select>
        </FormControl>
        {hasSummary && <LegalbirdTipTapEditor />}
      </Stack>
    </LegalbirdIoModal>
  );
}
